import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'

/** What the server sends: a notice that something changed, not the thing. */
type Update = {
  /** "project.task_was_completed" */
  action: string
  /** "project.task" */
  subject_type: string
  subject_id: string | null
  project_id: string | null
}

/**
 * Where the event stream lives. Same origin as the API, so the subscriber
 * cookie travels with it and there is no CORS.
 */
const HUB = '/.well-known/mercure'

/**
 * Which cached queries a given update makes wrong.
 *
 * Deliberately a little broad. Being too narrow means a screen quietly
 * showing stale data, which is the failure real-time exists to prevent; being
 * too broad costs one request that usually returns what is already there.
 */
function staleKeys(update: Update): unknown[][] {
  const keys: unknown[][] = []
  const [module] = update.action.split('.')

  if (update.project_id) {
    keys.push(['board', update.project_id])
    keys.push(['task-by-key'])
    keys.push(['projects'])
    keys.push(['project-report'])
    keys.push(['my-tasks'])
    keys.push(['widget'])
  }

  /*
    A notification is addressed to one person and arrives on their own topic,
    so it needs no project to be relevant - the bell has to change within the
    second wherever they happen to be looking.
  */
  if (update.subject_type === 'notification.notification') {
    keys.push(['notifications'])

    return keys
  }

  // Anything at all may have produced one, so the bell is always checked.
  keys.push(['notifications'])

  if (update.subject_type === 'comment.comment') keys.push(['comments'])
  if (update.subject_type === 'attachment.attachment') keys.push(['attachments'])
  if (module === 'user') {
    keys.push(['users'])
    keys.push(['roles'])
    keys.push(['project-members'])
  }

  // The log is a record of everything, so everything touches it.
  keys.push(['activity'])

  return keys
}

/**
 * Keeps the screen in step with what other people are doing.
 *
 * One connection for the whole application, opened once the person is signed
 * in. The server sends a notice rather than the changed record, and this
 * marks the matching queries stale - so the refetch goes through the ordinary
 * endpoints with the ordinary permission checks, and the hub never becomes a
 * second way to read data with its own idea of who may see what.
 *
 * `invalidateQueries` rather than writing into the cache: a screen nobody is
 * looking at is marked stale and refetches when it is next shown, while the
 * one on screen updates immediately. That is the behaviour people expect and
 * it costs nothing for the rest.
 */
export function useLiveUpdates(enabled: boolean): void {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return

    const source = subscribe(queryClient)

    return () => source.close()
  }, [enabled, queryClient])
}

function subscribe(queryClient: QueryClient): EventSource {
  const url = new URL(HUB, window.location.origin)

  /*
   * One wildcard subscription rather than one per project.
   *
   * The hub still delivers only the topics the cookie's token grants, so this
   * is not a way round authorisation - it is how the client avoids having to
   * resubscribe every time somebody opens a different board.
   */
  url.searchParams.append('topic', 'medulla/project/{id}')
  url.searchParams.append('topic', 'medulla/user/{id}')
  url.searchParams.append('topic', 'medulla/everyone')

  // withCredentials, or the browser will not attach the subscriber cookie.
  const source = new EventSource(url, { withCredentials: true })

  source.onmessage = (event: MessageEvent<string>) => {
    let update: Update

    try {
      update = JSON.parse(event.data) as Update
    } catch {
      // A message we cannot read is not a reason to tear down the stream.
      return
    }

    for (const queryKey of staleKeys(update)) {
      void queryClient.invalidateQueries({ queryKey })
    }
  }

  return source
}
